/**
 * @param {import("../Option").Option} option
 * @returns {boolean} option is parent
 */
export const isParent = option => Array.isArray(option?.innerValues)

/**
 * @param {string[]} values
 * @param {import("../Option").Option} option
 * @returns {boolean} checkbox is indeterminate
 */
export const isIndeterminate = (values, option) => {
  if (!isParent(option) || !option.innerValues) return false

  return option.innerValues.some((option) => values.includes(option.value) || isIndeterminate(values, option))
}

/**
 * @param {string[]} values
 * @param {import("../Option").Option[]} options
 * @returns {string[]} value is checked
 */
const expand = (options) => options.flatMap((option) => {
  if (option.innerValues) {
    return [option.value, ...expand(option.innerValues)]
  }
  return option.value
})

/**
 * @param {string[]} values
 * @param {import("../Option").Option[]} options
 * @returns {string[]} all selected values based on select state
 */
export const inflate = (values, options) => {
  return options.flatMap((option) => {
    if (!isParent(option)) {
      return values.includes(option.value) ? option.value : []
    } else {
      if (values.includes(option.value)) {
        return [option.value, ...expand(option.innerValues)]
      } else {
        if (!option.innerValues.length) return []

        const inner = inflate(values, option.innerValues)

        if (option.innerValues.every((option) => inner.includes(option.value))) {
          return [option.value, ...inner]
        }

        return inner
      }
    }
  })
}

/**
 * @param {string[]} values
 * @param {string[]} changes
 * @param {import("../Option").Option} option
 * @returns {boolean} value is removed
 */
const isRemoved = (values, changes, option) => values.includes(option.value) && changes.includes(option.value)

/**
 * @param {string[]} values
 * @param {string[]} changes
 * @param {import("../Option").Option} option
 * @returns {boolean} value is added
 */
const isAdded = (values, changes, option) => !values.includes(option.value) && changes.includes(option.value)

/**
 * @param {string[]} values
 * @param {string[]} changes
 * @param {import("../Option").Option[]} options
 * @returns {string[]} the upper most selected value
 */
export const deflate = (state, changes, options) => options.flatMap((option) => {
  if (!isParent(option)) {
    if (isAdded(state, changes, option)) {
      return option.value
    }

    return !isRemoved(state, changes, option) && state.includes(option.value) ? option.value : []
  } else if (option.innerValues.length) {
    if (isRemoved(state, changes, option)) {
      return []
    }

    if (isAdded(state, changes, option)) {
      return option.value
    }

    const inner = deflate(state, changes, option.innerValues)

    const isChecked = option.innerValues.every((innerValue) => inner.includes(innerValue.value))

    return isChecked ? option.value : inner
  } else {
    if (isAdded(state, changes, option)) {
      return option.value
    }

    if (isRemoved(state, changes, option)) {
      return []
    }

    return state.includes(option.value) ? option.value : []
  }
})

/**
 * @param {string[]} state
 * @param {import("../Option").Option[]} options
 * @returns {string[]} returns leaf values of selected option
 */
export const getLeafValues = (state, options) => options.flatMap((option) => {
  if (!isParent(option)) {
    return state.includes(option.value) ? option.value : []
  }

  return getLeafValues(state, option.innerValues)
})

/**
 * @param {string[]} values
 * @param {import("../Option").Option[]} options
 * @returns {string[]} returns leaf values of selected option
 */
export const getSelectedOptions = (values, options) => getLeafValues(inflate(values, options), options)
